import {
    useSignal,
    useSignalEffect,
    useSignals,
} from '@preact/signals-react/runtime'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
} from 'react'
import PhoneInput from 'react-phone-input-2'
import TranslateUtils from '../../i18n/TranslateUtils'
import {addressTypeOptions, addressTypes} from '../address/AddressConstant'
import {getListGenericAddress} from '../address/AddressEndPoint'
import {useAuth} from '../authentication/AuthProvider'
import {component} from '../form/Constant'
import {
    convertKeyToLabel,
    refetchEvent,
    removeReloadDataTable,
    tableEvent,
} from '../table-utils/TableUtils'
import {generalPractitionerKey} from './GeneralPractitionerContants'
import {
    useDetailGeneralPractitioner,
    useListGeneralPractitioners,
} from './query-keys/GeneralPractitionerKeysManager'

const QueryKey = 'General-Practitioner'

const ADDRESS_PATH = 'address'

const CountryPath = '/getCountries'
const StatePath = '/getStates'
const CityPath = '/getCities'

const CountryQueryKey = 'Countries-AddressForm'
const StateQueryKey = 'States-AddressForm'
const CityQueryKey = 'Cities-AddressForm'

const GeneralPractitionerUtils = forwardRef(
    (
        {
            customDetail = false,
            onChange = () => {},
            defaultValue = {},
            shouldTextBold = false,
        },
        ref,
    ) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
        }))

        //#region Translate
        const {
            translateKeys,
            translateCommons,
            translateAddressOptions,
            translateOptions,
        } = TranslateUtils()
        //#endregion

        //#region Context
        const {user} = useAuth()
        //#endregion

        //#region State
        const gpData = useSignal({...defaultValue})
        const selected = useSignal(null)
        const refCountry = useSignal()
        const refState = useSignal()
        const switchInputKeys = useSignal([])
        //#endregion

        //#region Query
        const queryClient = useQueryClient()

        const {data} = useDetailGeneralPractitioner(
            user?.id,
            selected.value,
            !!user?.id && !!selected.value,
        )
        const {data: countryOptions} = useQuery(
            [CountryQueryKey],
            async () =>
                await getAddressData(ADDRESS_PATH + CountryPath, user?.id),
            {
                refetchOnMount: false,
            },
        )

        const {data: stateOptions} = useQuery(
            [StateQueryKey, refCountry.value, switchInputKeys.value],
            async () =>
                await getAddressData(ADDRESS_PATH + StatePath, user?.id),
            {
                enabled:
                    !!refCountry.value &&
                    !switchInputKeys.value.includes('refState'),
                refetchOnMount: false,
            },
        )

        const {data: cityOptions} = useQuery(
            [
                CityQueryKey,
                refCountry.value,
                refState.value,
                switchInputKeys.value,
            ],
            async () => await getAddressData(ADDRESS_PATH + CityPath, user?.id),
            {
                enabled:
                    !!refCountry.value &&
                    !!refState.value &&
                    !switchInputKeys.value.includes('city') &&
                    !switchInputKeys.value.includes('refState'),
                refetchOnMount: false,
            },
        )

        const {data: gpOptions, refetch} = useListGeneralPractitioners(
            user?.id,
            true,
            {},
            !!user?.id,
        )

        const getAddressData = async (path, userId) => {
            return await await getListGenericAddress(
                path,
                userId,
                refCountry.value,
                refState.value,
            )
        }
        //#endregion

        //#region Memo
        const addressList = useMemo(() => {
            return [
                {
                    key: 'gpId',
                    component: component.dropdown,
                    label: 'Select GP',
                    placeholder: translateKeys('Select from options'),
                    options: gpOptions,
                },
                {
                    key: generalPractitionerKey.name,
                    component: component.inputText,
                    label: 'GP Name',
                },

                {
                    key: generalPractitionerKey.email,
                    component: component.inputText,
                    label: 'GP Email',
                },
                {
                    key: generalPractitionerKey.surgery,
                    component: component.inputText,
                    label: 'GP Surgery',
                },
                {
                    key: 'type',
                    label: 'Address Type',
                    placeholder: translateCommons('Optional'),
                    component: component.dropdown,
                    options: translateOptions(addressTypeOptions),
                    defaultValue: addressTypes.UNKNOWN.value,
                },
                {
                    key: 'refCountry',
                    label: 'GP Country',
                    placeholder: translateCommons('Optional'),
                    component: component.dropdown,
                    options: translateAddressOptions(countryOptions, 'country'),
                },
                {
                    key: 'refState',
                    label: 'GP State',
                    placeholder: translateCommons('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(stateOptions, 'state'),
                },
                {
                    key: 'city',
                    label: 'GP City',
                    placeholder: translateCommons('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(cityOptions, 'city'),
                },
                {
                    key: 'addressLine1',
                    label: 'GP Address Line 1',
                    placeholder: translateCommons('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine2',
                    label: 'GP Address Line 2',
                    placeholder: translateCommons('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine3',
                    label: 'GP Address Line 3',
                    placeholder: translateCommons('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine4',
                    label: 'GP Address Line 4',
                    placeholder: translateCommons('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'postalCode',
                    label: 'GP Postal Code',
                    placeholder: translateCommons('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'phone',
                    label: 'GP Phone',
                    component: component.phoneInput,
                },
            ]
        }, [
            translateKeys,
            gpOptions,
            translateCommons,
            translateOptions,
            translateAddressOptions,
            countryOptions,
            stateOptions,
            cityOptions,
        ])
        //#endregion

        //#region Handle Function
        const handleSwitchInput = useCallback(
            key => {
                let onChangeValue = {}

                if (switchInputKeys.value?.includes(key)) {
                    const inputKeys = switchInputKeys.value.filter(
                        x => x !== key,
                    )

                    switchInputKeys.value = inputKeys
                    if (key === 'refState') {
                        refState.value = null
                        onChangeValue = {
                            ...gpData.value,
                            refState: null,
                            state: '',
                            city: null,
                            addressConfig: {
                                inputFields: [...inputKeys],
                            },
                        }
                    } else if (key === 'city') {
                        onChangeValue = {
                            ...gpData.value,
                            city: null,
                            addressConfig: {
                                inputFields: [...inputKeys],
                            },
                        }
                    }
                } else {
                    if (
                        key === 'refState' &&
                        !switchInputKeys.value.includes('city')
                    ) {
                        const switchInputKeysValue = [
                            ...switchInputKeys.value,
                            key,
                            'city',
                        ]

                        onChangeValue = {
                            ...gpData.value,
                            refState: null,
                            state: '',
                            city: null,
                            addressConfig: {
                                inputFields: switchInputKeysValue,
                            },
                        }
                    } else if (key === 'city') {
                        switchInputKeys.value = [...switchInputKeys.value, key]
                        onChangeValue = {
                            ...gpData.value,
                            city: null,
                            addressConfig: {
                                inputFields: [...switchInputKeys.value, key],
                            },
                        }
                    }
                }

                onChange(onChangeValue)
                gpData.value = onChangeValue
            },
            [switchInputKeys, onChange, gpData, refState],
        )

        const handleDropdownChange = useCallback(
            (key, value) => {
                if (key === 'gpId') {
                    if (value) {
                        selected.value = value
                    } else {
                        selected.value = null
                        onChange({})
                        gpData.value = {}
                    }
                } else if (key === 'refCountry') {
                    refCountry.value = value
                    refState.value = null
                    onChange({...gpData.value, [key]: value})
                    gpData.value = {
                        ...gpData.value,
                        [key]: value,
                        refState: null,
                        city: null,
                    }
                } else if (key === 'refState') {
                    refState.value = value

                    let stateLabel =
                        stateOptions &&
                        stateOptions?.length &&
                        stateOptions?.find(item => item?.value === data)?.label

                    onChange({
                        ...gpData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? refState.value
                            : stateLabel,
                        city: null,
                    })
                    gpData.value = {
                        ...gpData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? refState.value
                            : stateLabel,
                        city: null,
                    }
                } else {
                    onChange({...gpData.value, [key]: value})
                    gpData.value = {
                        ...gpData.value,
                        [key]: value,
                    }
                }
            },
            [
                data,
                gpData,
                onChange,
                refCountry,
                refState,
                selected,
                stateOptions,
                switchInputKeys.value,
            ],
        )

        const handleGetValue = () => {
            let data = {
                ...gpData.value,
                gpId: selected.value,
            }
            return data
        }

        const {mutate: handleReload} = useMutation(data => data, {
            onSuccess: data => {
                if (!!data?.id) {
                    queryClient.setQueryData([QueryKey], oldData => [
                        {
                            value: data?.id,
                            label: data?.name,
                        },
                        ...oldData,
                    ])
                }
            },
        })

        const handleReloadData = useCallback(
            data => {
                if (!!data?.id) {
                    handleReload(data)
                }
            },
            [handleReload],
        )
        //#endregion

        //#region Effect
        useEffect(() => {
            if (!!defaultValue && !!defaultValue?.id) {
                selected.value = defaultValue?.id
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [defaultValue])

        useEffect(() => {
            if (!!data && !!selected.value) {
                if (data?.id === selected.value) {
                    let dataSelected = {
                        ...data,
                        gpId: data?.id,
                        refState:
                            data?.addressConfig &&
                            data.addressConfig?.inputFields?.length > 0 &&
                            data.addressConfig?.inputFields?.includes(
                                'refState',
                            )
                                ? data?.state
                                : data?.refState,
                    }
                    gpData.value = {...dataSelected}
                    onChange({
                        ...dataSelected,
                    })

                    switchInputKeys.value =
                        data?.addressConfig && data.addressConfig?.inputFields
                            ? [...data?.addressConfig.inputFields]
                            : []
                    refCountry.value =
                        data && data.refCountry ? data.refCountry : ''
                    refState.value =
                        data?.addressConfig &&
                        data.addressConfig?.inputFields?.length > 0 &&
                        data.addressConfig?.inputFields?.includes('refState')
                            ? data?.state
                            : data?.refState
                }
            } else {
                return
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [data, selected.value])

        useEffect(() => {
            if (!!QueryKey) {
                tableEvent.on('RELOAD-DATA-TABLE-' + QueryKey, handleReloadData)
                refetchEvent.on('REFETCH-DATA-TABLE-' + QueryKey, refetch)

                return () => {
                    removeReloadDataTable(QueryKey)
                    refetchEvent.removeListener(
                        'REFETCH-DATA-TABLE-' + QueryKey,
                    )
                }
            }
        }, [refetch, handleReloadData])
        // #endregion

        const panelVisible = useSignal(false)

        useSignalEffect(() => {
            const contentDiv = document.querySelector(
                '#blockscroll-sidebar .p-sidebar-content',
            )

            toggleBlockScroll(contentDiv, panelVisible.value)

            return () => {
                if (!!contentDiv) {
                    contentDiv.removeEventListener('wheel', preventScroll)
                    contentDiv.removeEventListener('touchmove', preventScroll)
                }
            }
        })

        const preventScroll = event => {
            event.preventDefault()
            event.stopPropagation()
        }

        const toggleBlockScroll = useCallback((contentDiv, isVisible) => {
            if (!!contentDiv) {
                if (!!isVisible) {
                    contentDiv.addEventListener('wheel', preventScroll, {
                        passive: false,
                    })
                    contentDiv.addEventListener('touchmove', preventScroll, {
                        passive: false,
                    })
                } else {
                    contentDiv.removeEventListener('wheel', preventScroll)
                    contentDiv.removeEventListener('touchmove', preventScroll)
                }
            }
        }, [])

        //#region Render
        const renderItem = useCallback(
            itemList => {
                return customDetail
                    ? itemList?.map(item => (
                          <div
                              key={item.key}
                              className={`col-12 lg:col-4 flex align-items-center p-0 mb-3`}>
                              {item.component === component.dropdown &&
                              !switchInputKeys.value?.includes(item?.key) ? (
                                  <div
                                      className={`flex gap-2 text-900 col-12 line-height-3`}>
                                      <div
                                          className={`flex-grow-1 p-float-label p-0`}>
                                          <Dropdown
                                              className={`${
                                                  item.className
                                              } ${'custom-detail'} col-12 h-3rem p-0 ${
                                                  shouldTextBold
                                                      ? 'font-semibold'
                                                      : ''
                                              }`}
                                              inputId={item.key}
                                              options={item.options || []}
                                              optionLabel="label"
                                              optionValue="value"
                                              filter
                                              emptyFilterMessage={translateCommons(
                                                  'No available options',
                                              )}
                                              emptyMessage={translateCommons(
                                                  'No results found',
                                              )}
                                              showClear
                                              disabled={
                                                  item.key !== 'gpId' &&
                                                  selected.value
                                                      ? true
                                                      : false
                                              }
                                              filterBy="label"
                                              value={
                                                  gpData.value
                                                      ? item.key === 'gpId'
                                                          ? gpData.value?.id
                                                          : gpData.value[
                                                                item.key
                                                            ]
                                                      : null
                                              }
                                              onChange={e =>
                                                  handleDropdownChange(
                                                      item.key,
                                                      e.value,
                                                  )
                                              }
                                              placeholder={translateKeys(
                                                  item.placeholder,
                                              )}
                                              style={{
                                                  '--inputColor':
                                                      'rgba(68, 184, 166, 0.1)',
                                              }}
                                              onShow={() => {
                                                  panelVisible.value = true
                                              }}
                                              onHide={() => {
                                                  panelVisible.value = false
                                              }}
                                          />
                                          <span className="bar"></span>
                                          <label htmlFor={item.key}>
                                              {translateKeys(item.label)}
                                          </label>
                                      </div>
                                      {item?.allowInput && (
                                          <div className="flex-none align-items-center justify-content-between p-0">
                                              <Button
                                                  disabled={
                                                      item.key !== 'gpId' &&
                                                      selected.value
                                                          ? true
                                                          : false
                                                  }
                                                  icon={
                                                      switchInputKeys.value?.includes(
                                                          item?.key,
                                                      )
                                                          ? 'pi pi-list'
                                                          : 'pi pi-pencil'
                                                  }
                                                  className="p-button-plain p-button-rounded"
                                                  onClick={() =>
                                                      handleSwitchInput(
                                                          item?.key,
                                                      )
                                                  }
                                                  tooltip={
                                                      switchInputKeys.value?.includes(
                                                          item?.key,
                                                      )
                                                          ? translateCommons(
                                                                'Switch to dropdown',
                                                            )
                                                          : translateCommons(
                                                                'Switch to input',
                                                            )
                                                  }
                                                  tooltipOptions={{
                                                      position: 'top',
                                                  }}
                                              />
                                          </div>
                                      )}
                                  </div>
                              ) : item.component === component.inputText ||
                                item.component === component.inputNumber ||
                                switchInputKeys.value?.includes(item?.key) ? (
                                  <div
                                      className={`flex gap-2 text-900 col-12 line-height-3`}>
                                      <div
                                          className={`flex-grow-1 p-float-label p-0`}>
                                          <InputText
                                              className={`custom-detail h-3rem col-12 ${
                                                  shouldTextBold
                                                      ? 'font-semibold'
                                                      : ''
                                              }`}
                                              value={
                                                  gpData.value &&
                                                  gpData.value[item.key]
                                                      ? gpData.value[item.key]
                                                      : ''
                                              }
                                              disabled={
                                                  selected.value ? true : false
                                              }
                                              onChange={e =>
                                                  handleDropdownChange(
                                                      item.key,
                                                      e.target.value,
                                                  )
                                              }
                                          />
                                          <span className="bar"></span>
                                          <label htmlFor={item.key}>
                                              {translateKeys(item.label)}
                                          </label>
                                      </div>
                                      {item?.allowInput && (
                                          <div className="flex-none align-items-center  p-0">
                                              <Button
                                                  disabled={
                                                      item.key !== 'gpId' &&
                                                      selected.value
                                                          ? true
                                                          : false
                                                  }
                                                  icon={
                                                      switchInputKeys.value?.includes(
                                                          item?.key,
                                                      )
                                                          ? 'pi pi-list'
                                                          : 'pi pi-pencil'
                                                  }
                                                  className="p-button-plain p-button-rounded"
                                                  onClick={() =>
                                                      handleSwitchInput(
                                                          item?.key,
                                                      )
                                                  }
                                                  tooltip={
                                                      switchInputKeys.value?.includes(
                                                          item?.key,
                                                      )
                                                          ? translateCommons(
                                                                'Switch to dropdown',
                                                            )
                                                          : translateCommons(
                                                                'Switch to input',
                                                            )
                                                  }
                                                  tooltipOptions={{
                                                      position: 'top',
                                                  }}
                                              />
                                          </div>
                                      )}
                                  </div>
                              ) : (
                                  item.component === component.phoneInput && (
                                      <React.Fragment>
                                          <div
                                              key={item.key}
                                              className="text-900 col-12 line-height-3">
                                              <span
                                                  className={`col-12 p-0 p-float-label`}>
                                                  <label
                                                      className="text-sm text-gray-700 absolute"
                                                      style={{
                                                          marginTop: '-35px',
                                                      }}
                                                      htmlFor={item.key}>
                                                      {translateKeys(
                                                          item.label,
                                                      )}
                                                  </label>
                                                  <div className="col-12 m-0 p-0">
                                                      <PhoneInput
                                                          id={item.key}
                                                          className={`col-12 h-3rem p-0 ${'custom-detail'} ${
                                                              shouldTextBold
                                                                  ? 'font-semibold'
                                                                  : ''
                                                          }`}
                                                          inputClass={`${
                                                              shouldTextBold
                                                                  ? 'font-semibold'
                                                                  : ''
                                                          }`}
                                                          placeholder={translateKeys(
                                                              item.label,
                                                          )}
                                                          enableSearch
                                                          country={'gb'}
                                                          disabled={
                                                              !!selected.value
                                                          }
                                                          preferredCountries={[
                                                              'gb',
                                                              'us',
                                                              'ca',
                                                              'vn',
                                                          ]}
                                                          inputStyle={{
                                                              minHeight: '40px',
                                                          }}
                                                          alwaysDefaultMask={
                                                              true
                                                          }
                                                          onChange={(
                                                              value,
                                                              country,
                                                              e,
                                                              formattedData,
                                                          ) =>
                                                              handleDropdownChange(
                                                                  item.key,
                                                                  formattedData,
                                                              )
                                                          }
                                                          value={
                                                              gpData.value
                                                                  ? gpData
                                                                        .value[
                                                                        item.key
                                                                    ]
                                                                  : ''
                                                          }
                                                      />
                                                      <span className="bar"></span>
                                                  </div>
                                              </span>
                                          </div>
                                      </React.Fragment>
                                  )
                              )}
                          </div>
                      ))
                    : itemList?.map(item => (
                          <div
                              key={item.key}
                              className={`col-12 lg:col-6  m-0 mb-1 flex align-items-center p-0`}>
                              {item.component === component.dropdown &&
                              !switchInputKeys.value?.includes(item?.key) ? (
                                  <div className={` col-12 line-height-3`}>
                                      {item?.allowInput ? (
                                          <div className="flex align-items-center justify-content-between relative ">
                                              <div>
                                                  <h5 className="font-bold">
                                                      {translateKeys(
                                                          item.label,
                                                      )}
                                                  </h5>
                                              </div>
                                              <div>
                                                  <Button
                                                      icon={
                                                          switchInputKeys.value?.includes(
                                                              item?.key,
                                                          )
                                                              ? 'pi pi-list'
                                                              : 'pi pi-pencil'
                                                      }
                                                      className="p-button-text p-button-plain p-button-rounded absolute right-0"
                                                      style={{top: '-10px'}}
                                                      onClick={() =>
                                                          handleSwitchInput(
                                                              item?.key,
                                                          )
                                                      }
                                                      tooltip={
                                                          switchInputKeys.value?.includes(
                                                              item?.key,
                                                          )
                                                              ? translateCommons(
                                                                    'Switch to dropdown',
                                                                )
                                                              : translateCommons(
                                                                    'Switch to input',
                                                                )
                                                      }
                                                      tooltipOptions={{
                                                          position: 'top',
                                                      }}
                                                  />
                                              </div>
                                          </div>
                                      ) : (
                                          <h5 className="font-bold">
                                              {translateKeys(item.label)}
                                          </h5>
                                      )}
                                      <Dropdown
                                          className={`${
                                              item.className
                                          }  col-12 h-3rem p-0 ${
                                              shouldTextBold
                                                  ? 'font-semibold'
                                                  : ''
                                          }`}
                                          inputId={item.key}
                                          options={item.options || []}
                                          optionLabel="label"
                                          optionValue="value"
                                          filter
                                          showClear
                                          disabled={
                                              item.key !== 'gpId' &&
                                              !!selected.value
                                          }
                                          filterBy="label"
                                          emptyFilterMessage={translateCommons(
                                              'No available options',
                                          )}
                                          emptyMessage={translateCommons(
                                              'No results found',
                                          )}
                                          value={
                                              gpData.value
                                                  ? gpData.value[item.key]
                                                  : ''
                                          }
                                          onChange={e =>
                                              handleDropdownChange(
                                                  item.key,
                                                  e.value,
                                              )
                                          }
                                          placeholder={translateKeys(
                                              item.placeholder,
                                          )}
                                          style={{
                                              '--inputColor':
                                                  'rgba(68, 184, 166, 0.1)',
                                          }}
                                          onShow={() => {
                                              panelVisible.value = true
                                          }}
                                          onHide={() => {
                                              panelVisible.value = false
                                          }}
                                      />
                                      <span className="bar"></span>
                                  </div>
                              ) : item.component === component.inputText ||
                                item.component === component.inputNumber ||
                                switchInputKeys.value?.includes(item?.key) ? (
                                  <div className={` col-12 line-height-3 `}>
                                      {item?.allowInput ? (
                                          <div className="flex align-items-center justify-content-between relative">
                                              <div>
                                                  <h5 className="font-bold">
                                                      {translateKeys(
                                                          item.label,
                                                      )}
                                                  </h5>
                                              </div>
                                              <div>
                                                  <Button
                                                      className="p-button-text p-button-plain p-button-rounded absolute right-0"
                                                      icon={
                                                          switchInputKeys.value?.includes(
                                                              item?.key,
                                                          )
                                                              ? 'pi pi-list'
                                                              : 'pi pi-pencil'
                                                      }
                                                      style={{top: '-10px'}}
                                                      onClick={() =>
                                                          handleSwitchInput(
                                                              item?.key,
                                                          )
                                                      }
                                                      tooltip={
                                                          switchInputKeys.value?.includes(
                                                              item?.key,
                                                          )
                                                              ? translateCommons(
                                                                    'Switch to dropdown',
                                                                )
                                                              : translateCommons(
                                                                    'Switch to input',
                                                                )
                                                      }
                                                      tooltipOptions={{
                                                          position: 'top',
                                                      }}
                                                  />
                                              </div>
                                          </div>
                                      ) : (
                                          <h5 className="font-bold">
                                              {translateKeys(item.label)}
                                          </h5>
                                      )}
                                      <InputText
                                          className={`h-3rem col-12 ${
                                              shouldTextBold
                                                  ? 'font-semibold'
                                                  : ''
                                          }`}
                                          value={
                                              gpData.value &&
                                              gpData.value[item.key]
                                                  ? gpData.value[item.key]
                                                  : ''
                                          }
                                          placeholder={`${translateKeys(
                                              'Input GP',
                                          )} ${translateKeys(
                                              convertKeyToLabel(
                                                  item.key,
                                              ).toLowerCase(),
                                          )} `}
                                          disabled={!!selected.value}
                                          onChange={e =>
                                              handleDropdownChange(
                                                  item.key,
                                                  e.target.value,
                                              )
                                          }
                                      />
                                  </div>
                              ) : (
                                  item.component === component.phoneInput && (
                                      <div className={` col-12 line-height-3 `}>
                                          <h5 className="font-bold">
                                              {translateKeys(item.label)}
                                          </h5>
                                          <PhoneInput
                                              id={item.key}
                                              className={`col-12 h-3rem p-0 ${'custom-detail'} ${
                                                  shouldTextBold
                                                      ? 'font-semibold'
                                                      : ''
                                              }`}
                                              inputClass={`${
                                                  shouldTextBold
                                                      ? 'font-semibold'
                                                      : ''
                                              }`}
                                              placeholder={translateKeys(
                                                  item.placeholder,
                                              )}
                                              enableSearch
                                              country={'gb'}
                                              disabled={!!selected.value}
                                              preferredCountries={[
                                                  'gb',
                                                  'us',
                                                  'ca',
                                                  'vn',
                                              ]}
                                              inputStyle={{
                                                  minHeight: '40px',
                                              }}
                                              alwaysDefaultMask={true}
                                              onChange={(
                                                  value,
                                                  country,
                                                  e,
                                                  formattedData,
                                              ) =>
                                                  handleDropdownChange(
                                                      item.key,
                                                      formattedData,
                                                  )
                                              }
                                              value={
                                                  gpData.value
                                                      ? gpData.value[item.key]
                                                      : ''
                                              }
                                          />
                                      </div>
                                  )
                              )}
                          </div>
                      ))
            },
            [
                customDetail,
                switchInputKeys.value,
                shouldTextBold,
                translateCommons,
                selected.value,
                gpData.value,
                translateKeys,
                handleDropdownChange,
                panelVisible,
                handleSwitchInput,
            ],
        )
        //#endregion

        return (
            <div className="grid col-12 m-0 p-0">{renderItem(addressList)}</div>
        )
    },
)

export default GeneralPractitionerUtils
